import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { metadataAPI } from '../api/enhancedApiService';

// Adaptive polling intervals (ms). Fast cadence kicks in only while a tier
// job is actively running or queued; idle connections poll on a long cadence
// so we're not burning server CPU + Supabase round-trips for nothing. These
// were 30s (per page override) / 60s (default) flat, which produced ~3
// tier-status calls per minute even with everything idle.
const TIER_POLL_ACTIVE_MS = 10_000;     // 10s while a tier is running/queued
const TIER_POLL_IDLE_MS = 120_000;      // 2min when nothing is running
const TIER_POLL_FIRST_PAINT_MS = 10_000; // 10s when we don't yet know — covers the case
                                        // where a user lands on the metadata tab while a
                                        // job is already running; we'd otherwise wait up
                                        // to 2 min for the first detection.

const _isTierActive = (tier) => {
  const status = (tier?.status || tier?.state || '').toLowerCase();
  return status === 'running' || status === 'queued' || status === 'in_progress';
};

const _hasActiveTier = (data) => {
  if (!data) return false;
  return ['tier1', 'tier2', 'tier3', 'tier4', 'tier5_lineage', 'tier6_usage']
    .some(key => _isTierActive(data[key]));
};

/**
 * Custom hook to fetch tiered metadata status
 * @param {string} connectionId - The connection ID to fetch tier status for
 * @param {Object} options - Additional options for the query
 * @returns {Object} Query result object with tier status
 */
export const useMetadataTierStatus = (connectionId, options = {}) => {
  const {
    enabled = !!connectionId,
    // Default to adaptive polling: fast while jobs are running, slow when
    // idle, moderate while we don't yet know. Callers can still pass a
    // numeric `refetchInterval` to override, and `false` disables polling
    // entirely.
    refetchInterval = (query) => {
      if (!connectionId) return false;
      const data = query?.state?.data;
      if (data === undefined) return TIER_POLL_FIRST_PAINT_MS;
      return _hasActiveTier(data) ? TIER_POLL_ACTIVE_MS : TIER_POLL_IDLE_MS;
    },
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['tier-status', connectionId],
    queryFn: ({ signal }) => metadataAPI.getTierStatus(connectionId, { forceFresh: true, signal }),
    enabled: enabled,
    refetchInterval: refetchInterval,
    ...queryOptions,
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
    // Transform the data to normalize API response
    select: (data) => {
      const responseData = data?.data || data;

      return {
        tier1: responseData?.tier1 || null,
        tier2: responseData?.tier2 || null,
        tier3: responseData?.tier3 || null,
        tier4: responseData?.tier4 || null,
        // Tier 5 Lineage (Starter+ plans, gated by lineage_enabled)
        tier5_lineage: responseData?.tier5_lineage || null,
        // Tier 6 Usage (Scale only, gated by enabled_tiers)
        tier6_usage: responseData?.tier6_usage || null,
        summary: responseData?.summary || {
          total_jobs: 0,
          successful_jobs: 0,
          failed_jobs: 0,
          tables_discovered: 0,
          last_collection_at: null
        },
        // Note: selection_mode removed - schema-first is the only mode
        selected_schemas: responseData?.selected_schemas || [],
        selected_tables: responseData?.selected_tables || []
      };
    }
  });
};

/**
 * Custom hook to fetch tier job history
 * @param {string} connectionId - The connection ID
 * @param {Object} options - Query options (limit, tier, status, etc.)
 * @returns {Object} Query result object with job history
 */
export const useMetadataTierJobs = (connectionId, options = {}) => {
  const {
    limit = 20,
    tier = null,
    status = null,
    enabled = !!connectionId,
    refetchInterval = false, // Don't auto-refresh job history by default
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['tier-jobs', connectionId, { limit, tier, status }],
    queryFn: ({ signal }) =>
      metadataAPI.getTierJobs(connectionId, { limit, tier, status, forceFresh: true, signal }),
    enabled: enabled,
    refetchInterval: refetchInterval,
    ...queryOptions,
    select: (data) => {
      const responseData = data?.data || data;
      return {
        jobs: responseData?.jobs || [],
        total: responseData?.total || 0,
        limit: responseData?.limit || limit
      };
    }
  });
};

/**
 * Custom hook to fetch detailed job information
 * @param {string} connectionId - The connection ID
 * @param {string} jobId - The job ID to fetch details for
 * @param {Object} options - Query options
 * @returns {Object} Query result object with job details
 */
export const useMetadataTierJob = (connectionId, jobId, options = {}) => {
  const {
    enabled = !!(connectionId && jobId),
    refetchInterval = false,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['tier-job', connectionId, jobId],
    queryFn: ({ signal }) =>
      metadataAPI.getTierJob(connectionId, jobId, { forceFresh: true, signal }),
    enabled: enabled,
    refetchInterval: refetchInterval,
    ...queryOptions,
    select: (data) => data?.data || data
  });
};

/**
 * Custom hook to fetch tier statistics
 * @param {string} connectionId - The connection ID
 * @param {Object} options - Query options (days, etc.)
 * @returns {Object} Query result object with tier statistics
 */
export const useMetadataTierStatistics = (connectionId, options = {}) => {
  const {
    days = 7,
    enabled = !!connectionId,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['tier-statistics', connectionId, days],
    queryFn: ({ signal }) =>
      metadataAPI.getTierStatistics(connectionId, { days, forceFresh: true, signal }),
    enabled: enabled,
    ...queryOptions,
    select: (data) => {
      const responseData = data?.data || data;
      return {
        period_days: responseData?.period_days || days,
        total_jobs: responseData?.total_jobs || 0,
        successful_jobs: responseData?.successful_jobs || 0,
        failed_jobs: responseData?.failed_jobs || 0,
        total_duration_seconds: responseData?.total_duration_seconds || 0,
        total_queries: responseData?.total_queries || 0,
        avg_duration_seconds: responseData?.avg_duration_seconds || 0,
        by_tier: responseData?.by_tier || {},
        daily_stats: responseData?.daily_stats || []
      };
    }
  });
};

/**
 * Custom hook to trigger a specific tier collection
 * @param {string} connectionId - The connection ID
 * @returns {Object} Mutation result object
 */
export const useTriggerTier = (connectionId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tierType, options = {} }) =>
      metadataAPI.triggerTier(connectionId, tierType, options),
    onSuccess: (data, variables) => {
      const { tierType } = variables;

      // Invalidate tier status to show updated state
      queryClient.invalidateQueries(['tier-status', connectionId]);

      // Invalidate job history
      queryClient.invalidateQueries(['tier-jobs', connectionId]);

      // After a delay, invalidate related queries
      setTimeout(() => {
        // Invalidate metadata queries if tier1 or tier2
        if (tierType === 'tier1_discovery' || tierType === 'tier2_schema') {
          queryClient.invalidateQueries(['metadata-status', connectionId]);
          queryClient.invalidateQueries(['schema-tables', connectionId]);
        }

        // Tier 4 regenerates auto-cached validation rules + results; the
        // validations page needs its caches cleared so users see updates
        // without hard-refreshing.
        if (tierType === 'tier4_profiling') {
          queryClient.invalidateQueries(['validations-summary', connectionId]);
          queryClient.invalidateQueries(['validation-rules']);
          queryClient.invalidateQueries({
            predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'table-validations',
          });
          queryClient.invalidateQueries(['validation-trends', connectionId]);
        }

        // Invalidate statistics
        queryClient.invalidateQueries(['tier-statistics', connectionId]);
      }, 2000);
    }
  });
};

/**
 * Custom hook to trigger all tiers for a connection
 * @param {string} connectionId - The connection ID
 * @returns {Object} Mutation result object
 */
export const useTriggerAllTiers = (connectionId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options = {}) =>
      metadataAPI.triggerAllTiers(connectionId, options),
    onSuccess: () => {
      // Invalidate all tier-related queries
      queryClient.invalidateQueries(['tier-status', connectionId]);
      queryClient.invalidateQueries(['tier-jobs', connectionId]);
      queryClient.invalidateQueries(['tier-statistics', connectionId]);

      // After a delay, invalidate metadata + downstream queries that depend
      // on tier outputs. Tier 4 in particular regenerates auto-cached
      // validation rules + their results, so the validations page needs
      // its caches cleared too — otherwise users see pre-run state until
      // they hard-refresh the browser.
      setTimeout(() => {
        queryClient.invalidateQueries(['metadata-status', connectionId]);
        queryClient.invalidateQueries(['schema-tables', connectionId]);
        queryClient.invalidateQueries(['connection-dashboard', connectionId]);
        // Validation caches — Tier 4 produces new auto-cached rule results
        queryClient.invalidateQueries(['validations-summary', connectionId]);
        queryClient.invalidateQueries(['validation-rules']);
        queryClient.invalidateQueries({
          predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'table-validations',
        });
        // Analytics / dashboards that aggregate validation data
        queryClient.invalidateQueries(['validation-trends', connectionId]);
        queryClient.invalidateQueries(['validation-history']);
      }, 2000);
    }
  });
};

/**
 * Custom hook to fetch system-wide tier statistics (admin)
 * @param {Object} options - Query options (days, etc.)
 * @returns {Object} Query result object with system statistics
 */
export const useSystemTierStatistics = (options = {}) => {
  const {
    days = 7,
    enabled = true,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['tier-system-statistics', days],
    queryFn: ({ signal }) =>
      metadataAPI.getSystemTierStatistics({ days, forceFresh: true, signal }),
    enabled: enabled,
    ...queryOptions,
    select: (data) => data?.data || data
  });
};

/**
 * Custom hook to fetch Tier 4 profiling data for a specific table
 * @param {string} connectionId - The connection ID
 * @param {string} tableId - The table ID to fetch profiling for
 * @param {Object} options - Query options
 * @returns {Object} Query result object with profiling data
 */
export const useTableProfiling = (connectionId, tableId, options = {}) => {
  const {
    enabled = !!(connectionId && tableId),
    refetchInterval = false, // Profiling data is static
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['table-profiling', connectionId, tableId],
    queryFn: ({ signal }) =>
      metadataAPI.getTableProfiling(connectionId, tableId, { forceFresh: true, signal }),
    enabled: enabled,
    refetchInterval: refetchInterval,
    ...queryOptions,
    select: (data) => {
      const responseData = data?.data || data;
      return {
        tableId: responseData?.table_id || tableId,
        tableName: responseData?.table_name || '',
        tableSchema: responseData?.table_schema || '',
        tableCatalog: responseData?.table_catalog || '',
        isProfiled: responseData?.is_profiled || false,
        profilingStrategy: responseData?.profiling_strategy || null,
        columns: responseData?.columns || [],
        profiledAt: responseData?.profiled_at || null,
        rowCount: responseData?.row_count || 0,
        sizeBytes: responseData?.size_bytes || 0
      };
    }
  });
};

/**
 * Custom hook to fetch profiling summary for all tables in a connection
 * @param {string} connectionId - The connection ID
 * @param {Object} options - Query options (schema filter, etc.)
 * @returns {Object} Query result object with profiling summary
 */
export const useConnectionProfilingSummary = (connectionId, options = {}) => {
  const {
    schema = null,
    enabled = !!connectionId,
    refetchInterval = false,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['connection-profiling-summary', connectionId, schema],
    queryFn: ({ signal }) =>
      metadataAPI.getConnectionProfilingSummary(connectionId, { schema, forceFresh: true, signal }),
    enabled: enabled,
    refetchInterval: refetchInterval,
    ...queryOptions,
    select: (data) => {
      const responseData = data?.data || data;
      return {
        totalTables: responseData?.total_tables || 0,
        profiledTables: responseData?.profiled_tables || 0,
        profilingCoverage: responseData?.profiling_coverage || 0,
        tables: responseData?.tables || []
      };
    }
  });
};
